import {
  ManageableAction,
  ManageableRequest,
} from "@p0security/shared/types/permission";
import { useAuthFetch } from "components/Login/hook";
import { useFlags } from "launchdarkly-react-client-sdk";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useManageableRequests = () => {
  const flags = useFlags();
  const enabled = flags.showManageableRequestsOnly ?? false;
  const [manageableRequests, setManageableRequests] = useState<
    ManageableRequest[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(enabled);
  const authFetch = useAuthFetch(undefined, setIsLoading);

  const refresh = useMemo(() => {
    const fetchRequests = async () => {
      if (!enabled) return;

      const result = await authFetch(`permission-requests/list-manageable`, {
        method: "GET",
      });

      if (!result?.ok) return;

      const data = await result.json();
      setManageableRequests(data);
      setIsPending(false);
    };

    return debounce(fetchRequests, 1000);
  }, [enabled, authFetch]);

  useEffect(() => {
    if (enabled) {
      refresh();
    }
  }, [enabled, refresh]);

  const isManageable = useCallback(
    (
      query: {
        anyOf?: ManageableAction[];
      },
      requestId: string
    ) => {
      if (!enabled) return true;

      const request = manageableRequests.find((r) => r.requestId === requestId);
      if (!request) return false;
      if (query.anyOf) {
        return query.anyOf.some((action) => request.actions.includes(action));
      }
      return false;
    },
    [enabled, manageableRequests]
  );

  return {
    isManageable,
    refresh,
    isLoading,
    isPending,
  };
};
